import get from 'lodash/get';
import React, { PropsWithChildren, ReactElement, useState } from 'react';
import { ActionMeta, GroupBase, OnChangeValue } from 'react-select';
import SelectType from 'react-select/base';
import CreatableSelect from 'react-select/creatable';
import { useTheme } from 'styled-components';

import {
  BaseAutocompleteOption,
  isOptionType,
  SUPPORTED_COMPONENTS,
} from '@savgroup-front-common/types';

import {
  safeFormattedIntlString,
  SafeFormattedMessage,
} from '../../../../formatters';
import { $Container, FieldMessage, Wrapper } from '../../common';
import { getFinalFieldState } from '../../common/helpers/getFinalFieldState';
import { Label } from '../../common/Label/Label';
import { useGetAutocompleteStyle } from '../Autocomplete.hooks';
import { $SelectWrapper } from '../Autocomplete.styles';
import * as customAutocompleteComponents from '../CustomAucompleteComponents';

import {
  getSelectedOption,
  sortOptions,
} from './CreatableAutocomplete.helpers';
import {
  AUTOCOMPLETE_ACTION_TYPES,
  CreatableAutocompleteProps,
} from './CreatableAutocomplete.types';
import messages from './messages';

const CreatableAutocomplete = <
  Option extends BaseAutocompleteOption & {
    __isNew__?: boolean;
  },
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: PropsWithChildren<CreatableAutocompleteProps<Option, IsMulti, Group>>,
  ref: React.ForwardedRef<SelectType<Option, IsMulti, Group>>,
): ReactElement => {
  const {
    name,
    label = null,
    postLabel,
    isRequired = false,
    value = null,
    options = [],
    isLoading = undefined,

    noOptionsMessage = messages.noOptionsMessage,
    loadingMessage = messages.loadingMessage,
    placeholder = messages.placeholder,
    createLabel = messages.createLabel,

    isDisabled = false,
    styles,
    defaultValue = null,
    componentThemeName = SUPPORTED_COMPONENTS.DEFAULT_AUTOCOMPLETE,
    isClearable,

    errors = {},
    warnings = {},
    successes = {},

    isError = false,
    isWarning = false,
    isSuccess = false,

    dataTestId,
    dataTestIdForCypress,

    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onChange,

    prefix = '',
    suffix = '',
    suffixButton,

    filterOption,
    withoutOptionSort = false,
    isAcceptDuplicateValuesMode = false,
    maxWidthOption,
    isMultiLineOption = false,
    ...rest
  } = props;

  const theme = useTheme();

  const [status, message] = getFinalFieldState({
    errors: { isStatus: isError, messages: errors },
    warnings: { isStatus: isWarning, messages: warnings },
    successes: { isStatus: isSuccess, messages: successes },
    name,
  });

  const defaultStyles = useGetAutocompleteStyle({
    status,
    maxWidthOption,
    isMultiLineOption,
  });

  const selectedValue = getSelectedOption(value, options);
  const defaultSelectedValue = getSelectedOption(defaultValue, options);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <$Container>
      {label && (
        <Label
          status={status}
          isRequired={isRequired}
          htmlFor={name}
          postLabel={postLabel}
        >
          {SafeFormattedMessage(label)}
        </Label>
      )}
      <Wrapper
        name={name}
        status={status}
        prefix={prefix}
        suffix={suffix}
        isHovered={isHovered}
        isFocused={isFocused}
        dataTestId={dataTestIdForCypress}
        suffixButton={suffixButton}
      >
        <$SelectWrapper
          data-testid={dataTestId}
          onMouseEnter={(e) => {
            if (onMouseEnter) {
              onMouseEnter(e);
            }
            setIsHovered(true);
          }}
          onMouseLeave={(e) => {
            if (onMouseLeave) {
              onMouseLeave(e);
            }
            setIsHovered(false);
          }}
        >
          <CreatableSelect
            {...rest}
            onChange={(
              option: OnChangeValue<Option, IsMulti>,
              action: ActionMeta<Option>,
            ) => {
              if (option && onChange) {
                if (isOptionType(option)) {
                  onChange(
                    {
                      ...option,
                      __isNew__: undefined,
                      isNew: get(option, '__isNew__'),
                    } as OnChangeValue<Option, IsMulti>,
                    action,
                  );
                } else {
                  if (
                    action.action === AUTOCOMPLETE_ACTION_TYPES.REMOVE_VALUE &&
                    isAcceptDuplicateValuesMode
                  ) {
                    const removedValueIndex = (options as Option[])
                      .map((item: Option) => item.label)
                      .indexOf(action.removedValue?.label);
                    const newOptions = (options as Option[]).reduce(
                      (
                        acc: Option[],
                        item: Option,
                        index: number,
                      ): Option[] => {
                        if (index === removedValueIndex) {
                          return acc;
                        }

                        return [...acc, item];
                      },
                      [],
                    );

                    onChange(
                      newOptions as unknown as OnChangeValue<Option, IsMulti>,
                      action,
                    );

                    return;
                  }
                  const newOptions = (option as Option[]).map(
                    (item: Option) => ({
                      ...item,
                      __isNew__: undefined,
                      isNew: get(item, '__isNew__', item.isNew),
                    }),
                  );

                  onChange(
                    newOptions as unknown as OnChangeValue<Option, IsMulti>,
                    action,
                  );
                }
              } else if (onChange) {
                onChange(option as OnChangeValue<Option, IsMulti>, action);
              }
            }}
            isLoading={isLoading}
            isDisabled={isDisabled}
            options={
              options && options.length > 0 && !withoutOptionSort
                ? sortOptions(options)
                : options
            }
            onFocus={(e: React.FocusEvent<HTMLInputElement, Element>) => {
              if (onFocus) {
                onFocus(e);
              }
              setIsFocused(true);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
              if (onBlur) {
                onBlur(e);
              }
              setIsFocused(false);
              setIsHovered(false);
            }}
            componentThemeName={componentThemeName as any}
            inputId={name}
            name={name}
            noOptionsMessage={() => safeFormattedIntlString(noOptionsMessage)}
            loadingMessage={() => safeFormattedIntlString(loadingMessage)}
            placeholder={safeFormattedIntlString(placeholder)}
            status={status}
            themeStyled={theme}
            styles={styles || defaultStyles}
            value={selectedValue}
            defaultValue={defaultSelectedValue}
            ref={ref}
            components={customAutocompleteComponents}
            filterOption={filterOption}
            isClearable={isClearable}
            formatCreateLabel={(inputValue: string) => {
              return safeFormattedIntlString(createLabel, { inputValue });
            }}
            isValidNewOption={
              isAcceptDuplicateValuesMode
                ? (inputValue: string) => {
                    if (!inputValue) {
                      return false;
                    }

                    return true;
                  }
                : undefined
            }
            getNewOptionData={
              isAcceptDuplicateValuesMode
                ? (_, optionLabel: React.ReactNode): Option => {
                    return {
                      value: optionLabel as string,
                      label: optionLabel as string,
                      __isNew__: true,
                    } as unknown as Option;
                  }
                : undefined
            }
          />
        </$SelectWrapper>
      </Wrapper>
      <FieldMessage message={message} status={status} dataTestId={dataTestId} />
    </$Container>
  );
};

export default React.forwardRef(CreatableAutocomplete) as <
  Option extends BaseAutocompleteOption & {
    __isNew__?: boolean;
  },
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: PropsWithChildren<
    CreatableAutocompleteProps<Option, IsMulti, Group>
  > & {
    ref?: React.ForwardedRef<SelectType<Option, IsMulti, Group>>;
  },
) => ReactElement;
